import { useMemo, RefObject } from 'react';
import { InstagramPostPreview } from '../../../widgets/instagram-post-preview';
import { MediaFile } from '../model/types';
import type { UserTag } from '../../../shared/lib/instagramTags';

interface Account {
  id: string;
  username?: string;
  name: string;
  avatarUrl?: string;
  profilePicture?: string;
}

interface Template {
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string;
  fontName?: string; // ← Добавляем fontName
  fontSize?: number; // ← Добавляем fontSize
  logoUrl?: string;
  logoSize?: number;
  logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  textUppercase?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  fontBold?: boolean;
  showLine?: boolean;
  lineColor?: string;
  lineHeight?: number;
  linePosition?: 'top' | 'bottom';
  applyFontEffects?: boolean;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  postLayout?: 'bottom-bar' | 'top-card' | 'frame';
  accentColor?: string;
  cardColor?: string;
  cardInset?: number;
  bandHeight?: number;
  frameUrl?: string;
  frameTextX?: number;
  frameTextY?: number;
  frameTextWidth?: number;
  frameTextVAlign?: 'top' | 'center' | 'bottom';
  autoFontMax?: number;
  bandGrow?: boolean;
}

interface UsePostPreviewParams {
  postType: 'single' | 'carousel';
  previewMode: 'simple' | 'template';
  media: MediaFile[];
  template?: Template;
  account?: Account;
  caption: string;
  title: string;
  templateRef: RefObject<HTMLDivElement>;
  onImageUpload: (files: File[]) => void;
  onImageUploadWithTemplate: (files: File[]) => void;
  fitMode?: 'fill' | 'fit';
  userTags?: UserTag[];
  onUserTagsChange?: (next: UserTag[]) => void;
  /** Рендер сообщает подобранный кегль и потолок плашки, preview-space px. */
  onResolvedFontSize?: (info: { fontSize: number; max: number }) => void;
}

interface UsePostPreviewReturn {
  previewElement: JSX.Element;
  canExport: boolean;
}

export function usePostPreview(params: UsePostPreviewParams): UsePostPreviewReturn {
  const {
    postType,
    previewMode,
    media,
    template,
    account,
    caption,
    title,
    templateRef,
    onImageUpload,
    onImageUploadWithTemplate,
    fitMode,
    userTags,
    onUserTagsChange,
    onResolvedFontSize,
  } = params;

  // Вычисляем можно ли экспортировать
  const canExport = useMemo(() => {
    return (
      previewMode === 'template' &&
      media.length > 0 &&
      !!template &&
      media[0].type === 'image'
    );
  }, [previewMode, media, template]);

  // Мемоизируем превью элемент
  const previewElement = useMemo(() => {
    // Нет аккаунта
    if (!account) {
      return (
        <div className="rounded-lg border p-8 text-center text-muted-foreground aspect-square flex items-center justify-center">
          Выберите аккаунт
        </div>
      );
    }

    // Single post + Simple mode
    if (postType === 'single' && previewMode === 'simple') {
      return (
        <InstagramPostPreview
          username={account.username || account.name}
          profilePicture={account.profilePicture}
          caption={caption}
          mode="simple"
          imageUrl={media[0]?.preview}
          mediaType={media[0]?.type}
          onImageUpload={onImageUpload}
          aspectRatio="square"
          fitMode={fitMode}
          userTags={userTags}
          onUserTagsChange={onUserTagsChange}
        />
      );
    }

    // Single post + Template mode
    if (postType === 'single' && previewMode === 'template') {
      if (!template) {
        return (
          <div className="rounded-lg border p-8 text-center text-muted-foreground aspect-square flex items-center justify-center">
            Выберите аккаунт с настроенным шаблоном
          </div>
        );
      }

      return (
        <InstagramPostPreview
          ref={templateRef}
          username={account.username || account.name}
          profilePicture={account.profilePicture}
          caption={caption}
          mode="template"
          imageUrl={media[0]?.preview}
          onImageUpload={onImageUploadWithTemplate}
          onResolvedFontSize={onResolvedFontSize}
          templateData={{
            backgroundColor: template.backgroundColor,
            backgroundColorEnd: template.backgroundColorEnd || template.backgroundColor,
            backgroundAlpha: template.backgroundAlpha !== undefined ? template.backgroundAlpha : 1,
            backgroundAlphaEnd: template.backgroundAlphaEnd !== undefined ? template.backgroundAlphaEnd : 1,
            textColor: template.textColor,
            useTwoColors: template.useTwoColors,
            textColorSecondary: template.textColorSecondary,
            fontFamily: template.fontFamily,
            fontUrl: template.fontUrl,
            fontName: template.fontName, // ← Добавляем fontName
            fontSize: template.fontSize, // ← Добавляем fontSize
            logoUrl: template.logoUrl,
            logoSize: template.logoSize, // без него превью рисует лого дефолтным размером
            text: title,
            logoPosition: template.logoPosition,
            useGradient: template.useGradient,
            textUppercase: template.textUppercase,
            textAlign: template.textAlign,
            textLineSpacing: template.textLineSpacing,
            fontBold: template.fontBold,
            showLine: template.showLine,
            lineColor: template.lineColor,
            lineHeight: template.lineHeight,
            linePosition: template.linePosition,
            textShadowEnabled: template.applyFontEffects === false ? false : template.textShadowEnabled,
            textShadowColor: template.textShadowColor,
            textShadowBlur: template.textShadowBlur,
            textShadowWidth: template.textShadowWidth,
            textSoftShadowEnabled: template.applyFontEffects === false ? false : template.textSoftShadowEnabled,
            textSoftShadowColor: template.textSoftShadowColor,
            textSoftShadowBlur: template.textSoftShadowBlur,
            textSoftShadowOffsetX: template.textSoftShadowOffsetX,
            textSoftShadowOffsetY: template.textSoftShadowOffsetY,
            textSoftShadowOpacity: template.textSoftShadowOpacity,
            textShadowOpacity: template.textShadowOpacity,
            postLayout: template.postLayout,
            accentColor: template.accentColor,
            cardColor: template.cardColor,
            cardInset: template.cardInset,
            bandHeight: template.bandHeight,
            frameUrl: template.frameUrl,
            frameTextX: template.frameTextX,
            frameTextY: template.frameTextY,
            frameTextWidth: template.frameTextWidth,
            frameTextVAlign: template.frameTextVAlign,
            autoFontMax: template.autoFontMax,
            bandGrow: template.bandGrow,
          }}
          fitMode={fitMode}
        />
      );
    }

    // Carousel + Simple mode
    if (postType === 'carousel' && previewMode === 'simple') {
      return (
        <InstagramPostPreview
          username={account.username || account.name}
          profilePicture={account.profilePicture}
          caption={caption}
          mode="simple"
          mediaItems={media.map(m => ({ url: m.preview, type: m.type }))}
          aspectRatio="square"
          showUploadButton={media.length === 0} // Показываем кнопку только если медиа нет
          onImageUpload={media.length === 0 ? onImageUpload : undefined} // Передаем callback только если медиа нет
          fitMode={fitMode}
        />
      );
    }

    // Carousel + Template mode
    if (postType === 'carousel' && previewMode === 'template') {
      if (!template) {
        return (
          <div className="rounded-lg border p-8 text-center text-muted-foreground aspect-square flex items-center justify-center">
            Выберите аккаунт с настроенным шаблоном
          </div>
        );
      }

      // Показываем карусель с шаблоном на первом изображении
      return (
        <InstagramPostPreview
          ref={templateRef}
          username={account.username || account.name}
          profilePicture={account.profilePicture}
          caption={caption}
          mode="simple"
          mediaItems={media.map(m => ({ url: m.preview, type: m.type }))}
          onResolvedFontSize={onResolvedFontSize}
          templateData={{
            backgroundColor: template.backgroundColor,
            backgroundColorEnd: template.backgroundColorEnd || template.backgroundColor,
            backgroundAlpha: template.backgroundAlpha !== undefined ? template.backgroundAlpha : 1,
            backgroundAlphaEnd: template.backgroundAlphaEnd !== undefined ? template.backgroundAlphaEnd : 1,
            textColor: template.textColor,
            useTwoColors: template.useTwoColors,
            textColorSecondary: template.textColorSecondary,
            fontFamily: template.fontFamily,
            fontUrl: template.fontUrl,
            fontName: template.fontName, // ← Добавляем fontName
            fontSize: template.fontSize, // ← Добавляем fontSize
            logoUrl: template.logoUrl,
            logoSize: template.logoSize, // без него превью рисует лого дефолтным размером
            text: title,
            logoPosition: template.logoPosition,
            useGradient: template.useGradient,
            textUppercase: template.textUppercase,
            textAlign: template.textAlign,
            textLineSpacing: template.textLineSpacing,
            fontBold: template.fontBold,
            showLine: template.showLine,
            lineColor: template.lineColor,
            lineHeight: template.lineHeight,
            linePosition: template.linePosition,
            textShadowEnabled: template.applyFontEffects === false ? false : template.textShadowEnabled,
            textShadowColor: template.textShadowColor,
            textShadowBlur: template.textShadowBlur,
            textShadowWidth: template.textShadowWidth,
            textSoftShadowEnabled: template.applyFontEffects === false ? false : template.textSoftShadowEnabled,
            textSoftShadowColor: template.textSoftShadowColor,
            textSoftShadowBlur: template.textSoftShadowBlur,
            textSoftShadowOffsetX: template.textSoftShadowOffsetX,
            textSoftShadowOffsetY: template.textSoftShadowOffsetY,
            textSoftShadowOpacity: template.textSoftShadowOpacity,
            textShadowOpacity: template.textShadowOpacity,
            postLayout: template.postLayout,
            accentColor: template.accentColor,
            cardColor: template.cardColor,
            cardInset: template.cardInset,
            bandHeight: template.bandHeight,
            frameUrl: template.frameUrl,
            frameTextX: template.frameTextX,
            frameTextY: template.frameTextY,
            frameTextWidth: template.frameTextWidth,
            frameTextVAlign: template.frameTextVAlign,
            autoFontMax: template.autoFontMax,
            bandGrow: template.bandGrow,
          }}
          applyTemplateToFirst={true}
          aspectRatio="square"
          showUploadButton={media.length === 0} // Показываем кнопку только если медиа нет
          onImageUpload={media.length === 0 ? onImageUploadWithTemplate : undefined} // Передаем callback только если медиа нет
          fitMode={fitMode}
        />
      );
    }

    // Fallback
    return (
      <div className="rounded-lg border p-8 text-center text-muted-foreground aspect-square flex items-center justify-center">
        Загрузите медиа
      </div>
    );
  }, [
    account,
    postType,
    previewMode,
    media,
    template,
    caption,
    title,
    templateRef,
    onImageUpload,
    onImageUploadWithTemplate,
    fitMode,
    userTags,
    onUserTagsChange,
    onResolvedFontSize,
  ]);

  return {
    previewElement,
    canExport,
  };
}